export type VersionedDefaultsSource = 'saved' | 'coded' | 'first-from-live';

export type ReconcileResult = {
  source: VersionedDefaultsSource;
  /** Saved preference exists but is stale (no/old version stamp) → drop & notify (info). */
  savedStale: boolean;
  /** Coded/generated defaults are themselves stale → error + log + first-from-live. */
  generatedStale: boolean;
};

/**
 * Decides which default-filter source to use.
 *
 * - `savedVersion` — the taxonomy version the saved preference was captured under
 *   (undefined for preferences saved before versioning existed).
 * - `currentVersion` — the live taxonomy version to compare against.
 * - `bakedVersion` — the version the generated (baked) defaults were built against
 *   (undefined for taxonomies without generated defaults, e.g. skill — those can
 *   only ever resolve to 'saved' or 'coded').
 */
export const reconcileVersionedDefaults = ({
  hasSaved,
  savedVersion,
  currentVersion,
  bakedVersion,
}: {
  hasSaved: boolean;
  savedVersion?: string;
  currentVersion?: string;
  bakedVersion?: string;
}): ReconcileResult => {
  // The baked/coded defaults themselves lag the live taxonomy.
  const generatedStale =
    bakedVersion !== undefined &&
    currentVersion !== undefined &&
    bakedVersion !== currentVersion;

  const codedFallback = (savedStale: boolean): ReconcileResult => ({
    source: generatedStale ? 'first-from-live' : 'coded',
    savedStale,
    generatedStale,
  });

  if (!hasSaved) {
    return codedFallback(false);
  }

  // A saved preference with no version stamp predates versioning, so its node ids
  // are from an unknown (older) taxonomy → always stale, notify and fall back.
  if (!savedVersion) {
    return codedFallback(true);
  }

  // Versioned saved pref but the live version is unavailable — don't false-alarm
  // a pref that already carries a version; trust it.
  if (!currentVersion) {
    return { source: 'saved', savedStale: false, generatedStale: false };
  }

  // Versioned saved pref vs a known live version.
  if (savedVersion === currentVersion) {
    return { source: 'saved', savedStale: false, generatedStale: false };
  }
  return codedFallback(true);
};
